import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RichMediaState } from '@bonusly/common-action-input';
import { useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useCurrentUser } from '@bonusly/who-am-i';

type FetchRecipientsProps = {
	include_self?: boolean;
	include_non_receivable?: boolean;
	search_usergroups?: boolean;
	search_users?: boolean;
};

type FetchUseRecipientsProps = {
	query: string;
	state: RichMediaState;
};

type RecipientAPIResponse = {
	data?: {
		result?: RecipientAPIData[] | null;
	};
};

const getRecipientResult = (response: RecipientAPIResponse | null | undefined): RecipientAPIData[] => {
	const result = response?.data?.result;

	return Array.isArray(result) ? result : [];
};

// @TODO reuse this logic for other common (feedback be sure not include groups)
export const useFetchRecipients = (params: FetchRecipientsProps = {}) => {
	const currentUser = useCurrentUser();
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');

	return useCallback(
		async ({ query, state }: FetchUseRecipientsProps): Promise<Recipient[]> => {
			const search = query.trim();
			const taggedRecipients = [...state.mentions, ...state.recipients];
			const { include_self, ...rest } = params;

			const buildRecipientsList = (data: RecipientAPIData[]) => {
				const recipients: Recipient[] = [];
				const seen = new Set<string>();
				data.forEach((r) => {
					const recipient = new Recipient(r);
					if (seen.has(recipient.id)) return;
					if (!include_self && recipient.type === 'username' && recipient.id === currentUser.id) {
						return;
					}
					if (!include_self && recipient.type === 'group') {
						// exclude self user from groups
						const recipients = recipient.userIds.filter((id) => id !== currentUser.id);
						recipient.userIds = recipients;
						// exclude group if empty
						if (recipients.length === 0) return;
					}
					if (taggedRecipients.some((tagged) => tagged.id === recipient.id)) {
						return;
					}
					seen.add(recipient.id);
					recipients.push(recipient);
				});
				return recipients;
			};

			if (!search) {
				const data = await queryClient.fetchQuery(['recipients'], () => http.get('/recipients'), { staleTime: Infinity });
				return buildRecipientsList(getRecipientResult(data));
			}

			const all = { search, ...rest };
			const data = await queryClient.fetchQuery(['rich-users', all], () => {
				return http.get('/users/autocomplete', { params: all });
			});
			return buildRecipientsList(getRecipientResult(data));
		},
		[currentUser, http, params, queryClient]
	);
};
